import { appAlert, appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState, useEffect, useMemo } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
import PmrUnmatched from "./PmrUnmatched";
import PmrMatchLines from "./PmrMatchLines";
import * as XLSX from "xlsx";
import { matchEstate } from "./estate_model";
import { loadEstateInputs, prescribedModelOf, estateRowsFor } from "./estate_data";
const StockExcess = ({
  onOpenOverview
}) => {
  const masked = useMaskNames();
  const [searchDrug, setSearchDrug] = useState("");
  const [showTransfers, setShowTransfers] = useState(false); // ✅ NEW
  const [transferData, setTransferData] = useState([]);
  // ✅ NEW
  // ✅ NEW
  const [monthsAhead, setMonthsAhead] = useState(3); // ✅ NEW
  const [monthsAverage, setMonthsAverage] = useState(3); // ✅ NEW
  // Same engine and same basis as the estate overview (2026-10). The basis
  // choice is shared with the overview through localStorage.
  const basis = "prescribed"; // Fixed demand basis; ignore old localStorage preference.
  const [estateInputs, setEstateInputs] = useState(null);
  const [estateError, setEstateError] = useState("");
  const [inventoryData, setInventoryData] = useState([]);
  const [usageNameMap, setUsageNameMap] = useState({});
  const [usageMonthlyMap, setUsageMonthlyMap] = useState({}); // key -> [{month, qty}]
  const [latestUsageMap, setLatestUsageMap] = useState({});
  const [usageCoverage, setUsageCoverage] = useState({}); // pharmacyId -> basis of the average

  const [onlyExcess, setOnlyExcess] = useState(false);
  // Every quantity in this component is TABLETS. Stock is packets x qtval,
  // usage is qty_used, excess is the difference - all units. Packets is a
  // display conversion only, and only possible where a pack size is known.
  const [showPacks, setShowPacks] = useState(false);
  const [minValue, setMinValue] = useState("");
  const [searchText, setSearchText] = useState("");
  const [expandedPicked, setExpandedPicked] = useState({});
  const [expandedReceived, setExpandedReceived] = useState({});
  const [expandedLog, setExpandedLog] = useState({});
  const [neededDialog, setNeededDialog] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState("");
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  // products a PMR upload could not map onto dm+d, review only
  const [showUnmatched, setShowUnmatched] = useState(false);
  // which excess rows / search rows have their PMR match breakdown open
  const [openMatches, setOpenMatches] = useState({});
  const [searchDrugName, setSearchDrugName] = useState("");
  const [searchNhs, setSearchNhs] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const groupId = localStorage.getItem("userId");
  const members = JSON.parse(localStorage.getItem("members") || "[]");
  useEffect(() => {
    fetchTransfers();
  }, []);
  useEffect(() => {
    fetchUsage();
  }, [monthsAverage]);
  // The figures and transfer suggestions come from the shared estate loader.
  const loadEstate = async () => {
    try {
      setEstateInputs(await loadEstateInputs({
        groupId,
        monthsAverage,
        prescribedOnly: true
      }));
      setEstateError("");
    } catch (e) {
      setEstateError(e.message || "Could not load the estate");
    }
  };
  useEffect(() => {
    loadEstate();
  }, [monthsAverage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchInventory();
    fetchUsage();
  }, []);

  // 📍 DISTANCE CALC
  const getDistance = (a, b) => {
    if (!a?.lat || !b?.lat) return 9999;
    const R = 6371;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLon = (b.lng - a.lng) * Math.PI / 180;
    const lat1 = a.lat * Math.PI / 180;
    const lat2 = b.lat * Math.PI / 180;
    const aVal = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return R * (2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal)));
  };

  // 🔧 NORMALISE
  const normaliseDrug = raw => {
    if (!raw) return {
      key: ""
    };
    let str = raw.toLowerCase();
    const strengthMatch = str.match(/\d+\s*(mg|g|ml|mcg)/);
    const strength = strengthMatch ? strengthMatch[0].replace(/\s+/g, "") : "";
    str = str.replace(/\(.*?\)/g, "").replace(/\d+\s*(mg|g|ml|mcg)/g, "").replace(/\d+\s*(tablet|capsule)/g, "").replace(/tablets?|capsules?/g, "").replace(/\s+/g, " ").trim();
    return {
      key: `${str}_${strength}`
    };
  };

  // units -> what the user asked to see. Falls back to tablets rather than
  // inventing a pack count when qtval is missing.
  const fmtQty = (units, qtval, dp = 0) => {
    if (units === null || units === undefined) return "-";
    if (showPacks && qtval > 0) return `${(units / qtval).toFixed(1)} pk`;
    return units.toFixed(dp);
  };
  const formatMonth = ym => {
    if (!ym || ym.length !== 6) return ym;
    const year = ym.substring(0, 4);
    const month = parseInt(ym.substring(4, 6), 10) - 1;
    return new Date(year, month).toLocaleString("en-GB", {
      month: "short",
      year: "numeric"
    });
  };
  const isStale = ym => {
    if (!ym || ym.length !== 6) return true;
    const year = parseInt(ym.substring(0, 4), 10);
    const month = parseInt(ym.substring(4, 6), 10) - 1;
    const usageDate = new Date(year, month);
    const now = new Date();
    const diffMonths = (now.getFullYear() - usageDate.getFullYear()) * 12 + (now.getMonth() - usageDate.getMonth());
    return diffMonths > 3;
  };

  // 📦 FETCH INVENTORY
  const fetchInventory = async () => {
    const token = localStorage.getItem("authToken");
    const res = await fetch(`${API_ORIGIN}/ggetInventory?groupId=${groupId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const data = await res.json();
    setInventoryData(data);
  };

  // 📊 FETCH USAGE
  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_ORIGIN}/getUsage3MonthsGroup?groupId=${groupId}&monthsBack=${monthsAverage}`,
      // ✅ MODIFIED

      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      const lookup = {};
      const latestMap = {};
      const coverage = {};
      const monthly = {};
      data.forEach(item => {
        const val = item.avgMonthlyUsage || 0;
        const parsed = normaliseDrug(item.drugName);
        // pharmacyId is the only identity used for matching anywhere in this screen.
        // The OCS code exists solely so an NHS extract can be resolved to a pharmacy
        // at import time; it is never a key, and a site without one must behave
        // exactly like a site with one.
        const siteId = item.pharmacyId;
        const key = `${siteId}_${parsed.key}`;
        lookup[key] = val;

        // several usage rows can share a key (brand and generic collapse onto the
        // same normalised name), so add the months together rather than overwrite
        if (Array.isArray(item.monthly)) {
          if (!monthly[key]) {
            monthly[key] = item.monthly.map(m => ({
              month: m.month,
              qty: m.qty || 0
            }));
          } else {
            item.monthly.forEach((m, i) => {
              if (monthly[key][i]) monthly[key][i].qty += m.qty || 0;
            });
          }
        }

        // the API now reports what the average was actually divided by, so the UI
        // can say so rather than leaving the user to assume it means "per month"
        if (!coverage[siteId]) {
          coverage[siteId] = {
            monthsLoaded: item.monthsLoaded ?? null,
            monthsInWindow: item.monthsInWindow ?? null,
            sources: item.sources || [],
            apportioned: !!item.apportioned
          };
        }
        if (item.dispensingmonth) {
          if (!latestMap[siteId] || item.dispensingmonth > latestMap[siteId]) {
            latestMap[siteId] = item.dispensingmonth;
          }
        }
      });
      setUsageNameMap(lookup);
      setLatestUsageMap(latestMap);
      setUsageCoverage(coverage);
      setUsageMonthlyMap(monthly);
    } catch (err) {
      console.log("❌ fetchUsage error:", err);
    }
  };

  // 🔥 THE ENGINE (2026-10): the estate overview's own model, not a second one.
  //   • spare = what is left after the branch's own cover, counted in whole
  //     packs, and only what survives the WORST month seen counts as movable;
  //   • pending transfers count on BOTH sides (out of the sender, into the
  //     receiver), so raising a transfer settles the figures;
  //   • branches whose usage is out of date are neither sources nor recipients;
  //   • the basis is either what was PRESCRIBED (a brand script needs that
  //     brand; a generic script takes any brand) or the generic dispensed view.
  const usePrescribed = true;
  const prescribedModel = useMemo(() => usePrescribed && estateInputs ? prescribedModelOf({
    requirePrescribed: true,
    prescribed: estateInputs.prescribed,
    members: estateInputs.members,
    inventoryData: estateInputs.inventoryData,
    identity: estateInputs.identity,
    monthsAhead,
    monthsAverage
  }) : null, [usePrescribed, estateInputs, monthsAhead, monthsAverage]);
  const estate = useMemo(() => estateInputs ? estateRowsFor({
    prescribedModel,
    inventoryData: estateInputs.inventoryData,
    usage: estateInputs.usage,
    members: estateInputs.members,
    monthsAhead,
    transferData: estateInputs.transferData,
    branchFreshness: estateInputs.branchFreshness
  }) : null, [prescribedModel, estateInputs, monthsAhead]);
  const model = useMemo(() => estate ? matchEstate(estate.rows) : null, [estate]);

  // 🔥 DISPLAY DATA — the model's rows in the shape this table has always shown.
  const displayData = useMemo(() => {
    const rows = estate?.rows || [];
    const hasUsage = r => r.knownZero || r.monthly && r.monthly.length > 0 || r.usage > 0;
    const shortOf = r => rows.filter(o => o.usable && o.deficitUnits > 0 && o.pharmacyId !== r.pharmacyId && (o.cleanName === r.cleanName || o.cleanName === r.altKey)).sort((a, b) => b.deficitUnits - a.deficitUnits).map(o => ({
      pharmacyName: o.pharmacyName,
      pharmacyId: o.pharmacyId,
      excess: -o.deficitUnits,
      qtval: r.packSize || o.packSize,
      viaGeneric: o.cleanName !== r.cleanName
    }));
    return rows.map(r => {
      const known = hasUsage(r);
      const excess = known ? r.excessUnitsSigned < 0 ? -r.deficitUnits : r.excessUnitsSigned : null;
      return {
        pharmacyName: r.pharmacyName,
        pharmacyId: r.pharmacyId,
        snomed: r.snomed,
        ocsCode: (estateInputs?.members || []).find(m => m.pharmacyId === r.pharmacyId)?.ocsCode || "",
        drugName: r.drugName,
        cleanName: r.cleanName,
        prescribedAs: r.prescribedAs,
        qtyOnShelf: r.qtyOnShelf,
        totalValue: r.totalValue,
        qtval: r.packSize || 1,
        usage3m: known ? r.usage : null,
        usageMonthly: r.monthly,
        excess,
        excessValue: excess !== null && r.unitValue > 0 ? excess * r.unitValue : null,
        certainPacks: r.certainExcessPacks,
        pendingQty: r.pendingOut,
        pendingIn: r.pendingIn,
        adjustedQty: r.adjustedQty,
        noStock: !!r.noStock,
        // Whether the engine will actually move it, and why not if not.
        canMove: !r.usable ? `No — ${r.branchWhy}` : r.certainExcessPacks > 0 ? r.unsureExcessPacks > 0 ? `${r.certainExcessPacks} of ${r.excessPacks} pk` : `Yes — ${r.certainExcessPacks} pk` : r.excessPacks > 0 ? `Held back — ${r.confidence.why}` : r.deficitPacks > 0 ? `Short ${r.deficitPacks} pk` : "—",
        confidence: r.confidence.level,
        neededByList: excess !== null && excess > 0 ? shortOf(r) : []
      };
    });
  }, [estate, estateInputs]);

  // 👁 WHAT THE STOCK LIST ACTUALLY SHOWS
  // The table body and the Excel export both read from here, so the export can
  // never hold a line the screen does not - the four filters live in one place.
  const visibleRows = useMemo(() => displayData.filter(r => {
    if (selectedPharmacy && r.pharmacyId !== selectedPharmacy) return false;
    if (onlyExcess && (!r.excess || r.excess <= 0)) return false;
    if (searchText && !r.drugName.toLowerCase().includes(searchText.toLowerCase())) return false;
    if (minValue && (r.excessValue === null || r.excessValue <= parseFloat(minValue))) return false;
    return true;
  }), [displayData, selectedPharmacy, onlyExcess, searchText, minValue]);

  // 🚚 TRANSFER SUGGESTIONS — straight from the shared engine: certain spare
  // only, largest need first, then whatever nobody needs to the warehouse.
  const suggestions = useMemo(() => {
    if (!model) return [];
    return [...model.transfers.map(t => ({
      drug: t.drugName,
      snomed: t.snomed,
      from: t.fromName,
      fromPharmacyId: t.fromPharmacyId,
      to: t.toName,
      toPharmacyId: t.toPharmacyId,
      qty: t.packs * t.packSize,
      qtval: t.packSize,
      value: t.value,
      why: t.why
    })), ...model.warehouse.map(w => ({
      drug: w.drugName,
      snomed: w.snomed,
      from: w.pharmacyName,
      fromPharmacyId: w.pharmacyId,
      to: "Warehouse",
      toPharmacyId: null,
      qty: w.packs * w.packSize,
      qtval: w.packSize,
      value: w.value,
      why: "Spare for certain and no branch in the estate is short of it.",
      status: "pending"
    }))];
  }, [model]);
  // 📧 SEND (UNCHANGED)
  const sendSuggestion = async s => {
    try {
      const token = localStorage.getItem("authToken");

      // Pack size comes with the suggestion itself (the sender's packs).
      const packSize = s.qtval || 1;

      // 🔥 convert tablets → packs
      const packs = Math.floor(s.qty / packSize);
      if (packs <= 0) {
        (await appAlert(`Less than one full pack (${packSize}). Cannot transfer.`));
        return;
      }
      const payload = {
        drug: s.drug,
        snomed: s.snomed,
        from: s.from,
        fromPharmacyId: s.fromPharmacyId,
        to: s.to,
        toPharmacyId: s.toPharmacyId,
        qtval: s.qtval,
        ...(s.status ? {
          status: s.status
        } : {}),
        qty: packs // ✅ SEND PACKS ONLY
      };
      console.log("🚀 SENDING (PACKS):", payload);
      const res = await fetch(apiUrl("/sendTransfer"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          groupId,
          ...payload
        })
      });
      const result = await res.json();
      console.log("📨 SEND RESULT:", result);
      if (!res.ok) {
        (await appAlert("Failed to send transfer"));
        return;
      }
      (await appAlert(`Sent ${packs} pack(s)`));
      await fetchTransfers();
      await fetchInventory();
      await loadEstate();
    } catch (err) {
      console.log("💥 sendSuggestion error:", err);
      (await appAlert("Error sending transfer"));
    }
  };

  // 📥 FETCH TRANSFERS
  const fetchTransfers = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
      // includeCompleted: a confirmed transfer is completed by definition, so
      // without this the row vanishes at the exact moment it is confirmed and
      // the confirmation looks like it did nothing. pendingTransferMap below
      // filters on status, so the wider list does not disturb any figure.

      // includeCompleted: a confirmed transfer is completed by definition, so
      // without this the row vanishes at the exact moment it is confirmed and
      // the confirmation looks like it did nothing. pendingTransferMap below
      // filters on status, so the wider list does not disturb any figure.
      `${API_ORIGIN}/getTransfers?groupId=${groupId}&includeCompleted=1`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      console.log("📊 TRANSFERS:", data.length);
      setTransferData(data);
    } catch (err) {
      console.log("💥 fetchTransfers error:", err);
    }
  };

  // 🗑 DELETE
  //
  // Takes the ROW, not a bare id. The list groups by sending branch and
  // destination only, so one displayed row can stand for several separate
  // transfer requests raised on different days for different drugs - and the
  // backend accepts a list, so the button reaches all of them. Passing t._id
  // alone deleted the first and left the rest showing on a row that claimed to
  // be gone.
  const deleteTransfer = async t => {
    const ids = t.sourceIds && t.sourceIds.length ? t.sourceIds : t._id ? [t._id] : [];
    if (!ids.length) {
      (await appAlert("This row has no transfer id attached, so it cannot be deleted. Refresh and try again."));
      return;
    }
    const items = (t.items || []).map(it => `${it.drug} (${it.qty})`).join(", ") || "this transfer";
    const dest = t.toPharmacyName || "the warehouse";
    const many = ids.length > 1 ? `\n\nThis row covers ${ids.length} separate transfer requests` + `${t.reference ? ` (${t.reference})` : ""}, and ALL of them will be deleted.` : "";
    if (!(await appConfirm(`Delete the transfer of ${items} from ${t.fromPharmacyName} to ${dest}?${many}`))) return;
    try {
      const token = localStorage.getItem("authToken");
      await fetch(`${API_ORIGIN}/deleteTransfer/${encodeURIComponent(ids.join(","))}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ids
        })
      });
      fetchTransfers();
      loadEstate();
    } catch (err) {
      console.log("💥 deleteTransfer error:", err);
    }
  };

  // ✅ COMPLETE
  //
  // Completing now MOVES STOCK at both ends, which it did not used to, so it
  // needs asking about just as much as deleting does - and it reaches every
  // request behind the row for the same reason delete does.
  const completeTransfer = async t => {
    const ids = t.sourceIds && t.sourceIds.length ? t.sourceIds : t._id ? [t._id] : [];
    if (!ids.length) {
      (await appAlert("This row has no transfer id attached, so it cannot be completed. Refresh and try again."));
      return;
    }
    const items = (t.items || []).map(it => `${it.drug} (${it.qty})`).join(", ") || "this transfer";
    const dest = t.toPharmacyName || "the warehouse";
    const many = ids.length > 1 ? `\n\nThis row covers ${ids.length} separate transfer requests` + `${t.reference ? ` (${t.reference})` : ""}, and ALL of them will be completed.` : "";
    if (!(await appConfirm(`Complete the transfer of ${items} from ${t.fromPharmacyName} to ${dest}?` + `\n\nThe packs come off ${t.fromPharmacyName}'s shelf and onto ${dest}'s.${many}`))) return;
    try {
      const token = localStorage.getItem("authToken");
      await fetch(`${API_ORIGIN}/completeTransfer/${encodeURIComponent(ids.join(","))}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ids
        })
      });
      fetchTransfers();
      loadEstate();
    } catch (err) {
      console.log("💥 completeTransfer error:", err);
    }
  };

  // 🧾 SEND INVOICE (stub for now)
  const sendInvoice = t => {
    console.log("📄 SEND INVOICE:", t);
    appAlert("Invoice function coming next");
  };
  const formatDateTime = d => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleDateString("en-GB") + " " + date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  const handleSearchUsage = async () => {
    if (!searchDrug && !searchNhs) {
      (await appAlert("Enter a drug name or NHS number"));
      return;
    }
    try {
      setSearchLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(apiUrl("/searchUsage"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          groupId,
          drug: searchDrug,
          nhs: searchNhs
        })
      });

      // surface failures instead of swallowing them - a 401, a CORS block and a
      // genuinely empty result all looked identical (an empty table) before this
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.log("💥 searchUsage HTTP", res.status, body);
        (await appAlert(`Search failed: HTTP ${res.status}${body ? `\n\n${body.slice(0, 300)}` : ""}`));
        setSearchResults([]);
        return;
      }
      const data = await res.json();
      if (!Array.isArray(data)) {
        console.log("💥 searchUsage unexpected payload:", data);
        (await appAlert(`Search returned an unexpected response:\n\n${JSON.stringify(data).slice(0, 300)}`));
        setSearchResults([]);
        return;
      }
      if (data.length === 0) {
        (await appAlert("No usage rows matched that search."));
      }
      setSearchResults(data);
    } catch (err) {
      console.log("💥 search error:", err);
      (await appAlert(`Search error: ${err.message}\n\nLikely a network or CORS failure - check the Network tab.`));
    } finally {
      setSearchLoading(false);
    }
  };
  // 📅 FORMAT DATE
  const formatDate = d => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleDateString("en-GB");
  };
  // Which feed is this file? Sniffed here so the pharmacy selector can appear
  // before the upload rather than after a round trip.
  //
  // An .xlsx is a ZIP, so readAsText() on one yields binary noise and the old
  // text-only version fell straight through to "that doesn't look like...".
  // The PMR dispensing export is xlsx, so it could never be selected at all.
  /* The usage upload lives in usage_upload.js now, shared with the summary
     screen. Its file sniffing, per-source pharmacy rule and result reporting
     used to sit here. */

  const selectedMember = members.find(m => m.pharmacyId === selectedPharmacy);
  // keyed by pharmacyId like everything else; the OCS code is not an identity
  const selectedSiteId = selectedPharmacy;
  const latestMonth = latestUsageMap[selectedSiteId];
  const stale = isStale(latestMonth);
  const formatDispensingMonth = val => {
    if (!val) return "";
    const str = String(val);

    // Extract year (first 4 digits always)
    const year = str.substring(0, 4);

    // Try to extract month (next 2 digits if present)
    let month = str.substring(4, 6);

    // Handle cases like YYYY-M or YYYY-MM or YYYYMM or YYYY/M
    if (!month || isNaN(month)) {
      const match = str.match(/^\d{4}[-\/]?(\d{1,2})/);
      if (match) {
        month = match[1];
      }
    }

    // Ensure 2-digit month
    if (month) {
      month = month.padStart(2, "0");
      return `${month}/${year}`;
    }
    return year; // fallback if no month found
  };

  // 📤 EXPORT TO EXCEL - the stock list exactly as it stands on screen
  //
  // Whatever the table shows is what goes out: the same visibleRows the table
  // renders, in the same order, in the same unit. The top of the sheet says
  // which organisation it belongs to, which filters were in force (or that none
  // were), what the figures rest on, and when it was printed - a spreadsheet
  // that turns up in an inbox a week later has to explain itself.
  //
  // Numbers are written as numbers, not text, so Excel can total a column.
  // Precision matches the screen: whole tablets, one decimal for packs, usage
  // and excess, pence on money. Needed by lists every branch, where the screen
  // only has room for the first two.
  const exportStockList = () => {
    const rows = visibleRows;
    if (rows.length === 0) {
      appAlert("Nothing to export - no lines match the current filters.");
      return;
    }
    const orgName = localStorage.getItem("organisationName") || "";
    const now = new Date();
    const printedOn = now.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }) + " " + now.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    });
    const unit = showPacks ? "packets" : "tablets";

    // Only the filters that are actually narrowing the list. A control left at
    // its default is not a filter, and "Pharmacy: All" would send the reader
    // checking something that changed nothing.
    const filters = [];
    if (selectedPharmacy) {
      filters.push(["Pharmacy", displayName(selectedMember?.pharmacyName || selectedPharmacy, masked)]);
    }
    if (searchText.trim()) filters.push(["Drug name contains", searchText.trim()]);
    if (onlyExcess) filters.push(["Show excess only", "Yes"]);
    if (minValue !== "" && minValue !== null && !isNaN(parseFloat(minValue))) {
      filters.push(["Only excess value greater than", `£${parseFloat(minValue).toFixed(2)}`]);
    }

    // One number the way the screen shows it: packets where the pack size is
    // known and the toggle is on, otherwise tablets - and, in packet mode, a
    // Unit column says which per row, since the fallback is invisible in a
    // column of bare numbers.
    const qtyOut = (units, qtval, dp) => {
      if (units === null || units === undefined) return "";
      if (showPacks && qtval > 0) return Number((units / qtval).toFixed(1));
      return Number(Number(units).toFixed(dp));
    };
    const unitOf = qtval => showPacks && qtval > 0 ? "packets" : "tablets";
    const header = ["Pharmacy", "Drug", `Stock level (${unit})`, `Pending out (${unit})`, `Usage by month (${unit})`, `Usage/mth average (${unit})`, `Excess (${unit})`, "Excess value (£)", "Can it move?", "Needed by"];
    if (showPacks) header.push("Unit");
    const body = rows.map(row => {
      const line = [displayName(row.pharmacyName, masked), row.drugName, qtyOut(row.adjustedQty, row.qtval, 0), row.pendingQty > 0 ? qtyOut(row.pendingQty, row.qtval, 0) : "", row.usage3m === null ? "N/A" : (row.usageMonthly || []).map(m => qtyOut(m.qty, row.qtval, 1)).join(" | "), row.usage3m === null ? "N/A" : qtyOut(row.usage3m, row.qtval, 1), row.excess !== null ? qtyOut(row.excess, row.qtval, 1) : "", row.excessValue !== null ? Number(row.excessValue.toFixed(2)) : "", row.canMove || "", (row.neededByList || []).map(r => displayName(r.pharmacyName, masked)).join(", ")];
      if (showPacks) line.push(unitOf(row.qtval));
      return line;
    });
    const top = [[orgName || "(organisation name not recorded)"], ["Stock list - Analysis and transfer (detailed)"], ["Printed", printedOn], [], ["Filters applied"], ...(filters.length ? filters : [["None - every line on the list is included"]]), [], ["Figures based on"], ["Months ahead to use", monthsAhead], ["Months of usage averaged", monthsAverage], ["Worked out against", usePrescribed ? "What was prescribed" : "Generic (dispensed)"], ["Quantities in", unit + (showPacks ? " (tablets where no pack size is known - see the Unit column)" : "")], ["Lines", rows.length], []];
    const ws = XLSX.utils.aoa_to_sheet([...top, header, ...body]);
    ws["!cols"] = [{
      wch: 30
    }, {
      wch: 48
    }, {
      wch: 18
    }, {
      wch: 18
    }, {
      wch: 26
    }, {
      wch: 22
    }, {
      wch: 16
    }, {
      wch: 16
    }, {
      wch: 44
    }, {
      wch: 10
    }];
    // Excel's own filter arrows on the column headings, so the reader can
    // narrow the list further without touching the numbers above it.
    const headerRow = top.length; // zero-based row index of the column headings
    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        r: headerRow,
        c: 0
      }, {
        r: headerRow + body.length,
        c: header.length - 1
      })
    };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock list");
    const slug = (orgName || "stock").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "stock";
    const stamp = now.toISOString().slice(0, 10);
    XLSX.writeFile(wb, `stock_list_${slug}_${stamp}.xlsx`);
  };
  return <div style={{
    padding: "20px"
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>
        <h1 style={{
        margin: 0
      }}>Stock Redistribution Engine</h1>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12
      }}>
          <MaskToggleButton />
          {/* The way back up. This screen has no menu entry of its own - it is
              reached from the Detailed button on the estate overview - so
              without this there is no route out except the browser. Sits top
              right, where the matching Detailed button sits on the overview. */}
          {onOpenOverview && <button onClick={onOpenOverview} title="Back up to the estate stock and warehouse overview" style={{
          padding: "8px 14px",
          fontWeight: 600
        }}>
              &#8592; Overview
            </button>}
        </div>
      </div>

<div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      margin: "12px 0",
      padding: "10px 12px",
      border: "1px solid #dfe3e8",
      borderRadius: 8,
      background: "#fff"
    }}>
  <strong>Work out stock against</strong>
  <span style={{
        fontWeight: 600
      }}>What was prescribed</span>
  <span style={{
        fontSize: "0.88em",
        color: "#5D5D5D"
      }}>
    Same engine as the overview: only stock that survives the worst month seen is offered, pending transfers count at both ends,
    out-of-date branches are left out, and the branch shortest of a product is served first.
    {basis === "prescribed" && " A brand prescription needs that brand; a generic prescription takes any brand."}
  </span>
  {basis === "prescribed" && estateInputs && !estateInputs.prescribed?.available && <span style={{
        color: "#AD4E00",
        fontSize: "0.88em"
      }}>No prescribed usage uploaded yet — prescribed demand is unavailable.</span>}
  {prescribedModel?.built.stats.unresolvedStockCount > 0 && <span style={{
        color: "#AD4E00",
        fontSize: 13
      }}> · {prescribedModel.built.stats.unresolvedStockCount} stock line(s) need product identity or pack quantity; excluded from excess calculations.</span>}
  {prescribedModel?.built.stats.unmatched > 0 && <span style={{
        color: "#AD4E00",
        fontSize: "0.88em"
      }}>{prescribedModel.built.stats.unmatched} prescribed line(s) not yet matched to dm+d are left out.</span>}
  {!estateInputs && !estateError && <span style={{
        fontSize: "0.88em",
        color: "#5D5D5D"
      }}>Loading…</span>}
  {estateError && <span style={{
        color: "#A8071A",
        fontSize: "0.88em"
      }}>{estateError}</span>}
</div>

<div style={{
      marginBottom: "20px"
    }}>

  <button style={{
        marginLeft: "10px"
      }} onClick={() => {
        setSearchResults([]);
        setSearchDrugName("");
        setSearchNhs("");
        setShowSearchDialog(true);
      }}>
    Search Usage
  </button>


</div>



<div style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "10px"
    }}>
  <select value={selectedPharmacy} onChange={e => setSelectedPharmacy(e.target.value)}>
    <option value="">All Pharmacies</option>
    {members.map(m => <option key={m.pharmacyId} value={m.pharmacyId}>
        {displayName(m.pharmacyName, masked)}
      </option>)}
  </select>

  <input placeholder="Search drug..." value={searchText} onChange={e => setSearchText(e.target.value)} />
</div>
      <label style={{
      marginLeft: "10px"
    }}>
        <span style={{
        marginLeft: "20px"
      }}>
  Months ahead to use:
  <select value={monthsAhead} onChange={e => setMonthsAhead(parseInt(e.target.value))} style={{
          marginLeft: "5px"
        }}>
    {[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
  </select>
</span>

<span style={{
        marginLeft: "20px"
      }}>
  Months usage to average:
  <select value={monthsAverage} onChange={e => setMonthsAverage(parseInt(e.target.value))} style={{
          marginLeft: "5px"
        }}>
  {[1, 2, 3].map(v => <option key={v} value={v}>{v}</option>)}
  </select>
</span>
        <input type="checkbox" checked={onlyExcess} onChange={() => setOnlyExcess(!onlyExcess)} />
        Show excess only
      </label>
<span style={{
      marginLeft: "20px"
    }}>
  <label style={{
        cursor: "pointer"
      }}>
    <input type="checkbox" checked={showPacks} onChange={() => setShowPacks(!showPacks)} />
    {" "}Show packets instead of tablets
  </label>
</span>

<span style={{
      marginLeft: "20px"
    }}>
  Only show value &gt; £
  <input type="number" value={minValue} onChange={e => setMinValue(e.target.value)} style={{
        width: "80px",
        marginLeft: "5px"
      }} />
</span>

{/* What the Usage/mth column is actually based on. Without this the number
    reads as "per month" with no clue how many months it averaged over, or
    that PMR figures are apportioned across a date range. */}
{(() => {
      const cov = usageCoverage[selectedSiteId];
      const anyCov = Object.keys(usageCoverage).length > 0;
      if (!selectedPharmacy && !anyCov) return null;
      if (selectedPharmacy && !cov) {
        return <div style={{
          marginTop: "12px",
          padding: "8px 10px",
          background: "#fff4f4",
          border: "1px solid #e0b4b4",
          fontSize: "0.9em"
        }}>
        <strong>No usage data for this pharmacy</strong> in the last {monthsAverage}&nbsp;month
        {monthsAverage === 1 ? "" : "s"}. Excess cannot be calculated without it &mdash;
        upload an NHS dispensing extract or a PMR usage report above.
      </div>;
      }
      const shown = cov || Object.values(usageCoverage)[0];
      const loaded = shown?.monthsLoaded;
      const window_ = shown?.monthsInWindow ?? monthsAverage;
      const partial = loaded != null && window_ != null && loaded < window_;
      return <div style={{
        marginTop: "12px",
        padding: "8px 10px",
        background: partial || stale ? "#fffaf0" : "#f6f8fa",
        border: `1px solid ${partial || stale ? "#e6c98a" : "#dfe3e8"}`,
        fontSize: "0.9em",
        lineHeight: 1.45
      }}>
      <strong>Usage/mth</strong> is averaged over each branch&rsquo;s latest {monthsAverage} full
      month{monthsAverage === 1 ? "" : "s"} (a month not yet fully reported is skipped in favour of the one
      before), the same window the overview uses; a full month with none used counts as zero.
      {usePrescribed ? " On the prescribed basis it is what was prescribed, from the paired usage report." : ""}
      {" "}{loaded != null && <>Found <strong>{loaded}</strong> month{loaded === 1 ? "" : "s"} of data
        within the last {window_}.</>}
      {partial && <> <span style={{
            color: "#a05a00"
          }}>Fewer months of data than the window means
        the figure rests on a shorter run than you selected.</span></>}
      {shown?.apportioned && <><br />Some or all of this pharmacy&rsquo;s usage comes from a PMR report, which
        covers a date range rather than whole months. Its total is shared across the
        months it spans in proportion to the days in each, so figures can be fractional.</>}
      {latestMonth && <><br />Most recent month of data: <strong>{formatDispensingMonth(latestMonth)}</strong>.
        {stale && <span style={{
            color: "#a00",
            fontWeight: "bold"
          }}>
            {" "}This is more than 3 months old &mdash; excess figures may be misleading.
          </span>}</>}
    </div>;
    })()}

      {/* The list's own toolbar: how many lines the filters have left, and
          the export of exactly those lines. Sits on the table rather than up
          among the filter rows so it reads as "export this list". */}
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "20px",
      marginBottom: "6px"
    }}>
        <span style={{
        fontSize: "0.9em",
        color: "#555"
      }}>
          {visibleRows.length} line{visibleRows.length === 1 ? "" : "s"} shown
          {visibleRows.length !== displayData.length && ` of ${displayData.length}`}
        </span>
        <button onClick={exportStockList} disabled={visibleRows.length === 0} title="Download the lines shown below as an Excel spreadsheet - the same lines, filters and units as on screen, headed with the organisation name and the date printed" style={{
        padding: "6px 12px",
        fontWeight: 600
      }}>
          Export to Excel
        </button>
      </div>
      <div style={{
      maxHeight: "400px",
      overflowY: "auto",
      border: "1px solid #ccc"
    }}>
        <table border="1" cellPadding="6" style={{
        width: "100%"
      }}>
     <thead style={{
          position: "sticky",
          top: 0,
          background: "#eee"
        }}>
  <tr>
    <th>Pharmacy</th>
    <th>Drug</th>
    <th>Stock level{showPacks ? " (packets)" : " (tablets)"}</th>
    <th>Usage/mth{showPacks ? " (packets)" : " (tablets)"}</th>
    <th>Excess{showPacks ? " (packets)" : " (tablets)"}</th>
    <th>Excess Value</th>
    <th title="Whether the transfer engine will move it: only stock that survives the worst month seen, at a branch with current usage">Can it move?</th>
    <th>Needed By</th>
  </tr>
</thead>

          <tbody>
            {/* filtered once in visibleRows (above), shared with the export */}
            {visibleRows.map((row, i) => <React.Fragment key={i}>
                <tr style={{
              background: row.certainPacks > 0 ? "#d4edda" : row.excess > 0 ? "#f3f9ee" : row.excess < 0 ? "#fdf2f2" : "white"
            }}>
                  <td>{displayName(row.pharmacyName, masked)}</td>
                  {/* the dm+d name is the OUTPUT of a match, so it opens to
                      show which PMR lines were folded into it */}
                  <td style={{
                cursor: "pointer"
              }} title="Show what is counted under this dm+d line" onClick={() => setOpenMatches(prev => ({
                ...prev,
                [`x${row.pharmacyId}_${row.snomed}`]: !prev[`x${row.pharmacyId}_${row.snomed}`]
              }))}>
                    {openMatches[`x${row.pharmacyId}_${row.snomed}`] ? "\u25be " : "\u25b8 "}
                    {row.drugName}
                    {row.prescribedAs && row.prescribedAs.length > 0 && <div style={{
                  fontSize: "0.8em",
                  color: "#5D5D5D"
                }}>Prescribed as: {row.prescribedAs.slice(0, 2).join("; ")}{row.prescribedAs.length > 2 ? "…" : ""}</div>}
                  </td>
              <td>
  {fmtQty(row.adjustedQty, row.qtval)}
  {row.pendingQty > 0 && <span style={{
                  color: "#666"
                }}>
  {" "}({fmtQty(row.pendingQty, row.qtval)} pending out)
</span>}
{row.pendingIn > 0 && <span style={{
                  color: "#666"
                }}>
  {" "}({fmtQty(row.pendingIn, row.qtval)} on its way in)
</span>}
</td>
                  <td>
  {row.usage3m === null ? "N/A" : <>
      {row.usageMonthly && row.usageMonthly.length > 0 && <div style={{
                    fontSize: "0.85em",
                    color: "#555"
                  }}>
          {row.usageMonthly.map(m => fmtQty(m.qty, row.qtval, 1)).join("  |  ")}
        </div>}
      <div>Av: {fmtQty(row.usage3m, row.qtval, 1)}</div>
    </>}
</td>
              <td>{row.excess !== null ? fmtQty(row.excess, row.qtval, 1) : "-"}</td>

<td>
  {row.excessValue !== null ? `£${row.excessValue.toFixed(2)}` : "-"}
</td>

<td style={{
                fontSize: "0.88em"
              }}>{row.canMove}</td>

<td>
  {row.neededByList && row.neededByList.length > 0 ? <span style={{
                  cursor: "pointer",
                  color: "blue"
                }} onClick={() => setNeededDialog(row)}>
      {row.neededByList.slice(0, 2).map(r => r.pharmacyName).join(", ")}
      {row.neededByList.length > 2 && "..."}
    </span> : "-"}
</td>
                </tr>

                {openMatches[`x${row.pharmacyId}_${row.snomed}`] && <tr>
                    <td colSpan={10} style={{
                padding: 0
              }}>
                      <PmrMatchLines groupId={groupId} snomed={row.snomed} pharmacyId={row.pharmacyId} onChanged={fetchUsage} />
                    </td>
                  </tr>}
                </React.Fragment>)}
          </tbody>
        </table>
      </div>

      <h2 style={{
      marginTop: "30px"
    }}>Transfer Suggestions</h2>

      <div style={{
      maxHeight: "250px",
      overflowY: "auto",
      border: "1px solid #ccc"
    }}>
        <table border="1" cellPadding="6" style={{
        width: "100%"
      }}>
          <thead style={{
          position: "sticky",
          top: 0,
          background: "#eee"
        }}>
            <tr>
              <th>Drug</th>
              <th>From</th>
              <th>To</th>
              <th>Qty{showPacks ? " (packets)" : " (tablets)"}</th>
              <th>Value</th>
              <th>Why</th>
              <th>Send</th>
            </tr>
          </thead>

          <tbody>
            {suggestions.map((s, i) => <tr key={i}>
                <td>{s.drug}</td>
                <td>{s.from}</td>
                <td>{s.to}</td>
                <td>{fmtQty(s.qty, s.qtval)}</td>
                <td>{s.value != null ? `£${Number(s.value).toFixed(2)}` : "-"}</td>
                <td style={{
              fontSize: "0.85em",
              color: "#5D5D5D",
              maxWidth: 420
            }}>{s.why}</td>
                 <td>
                  <button onClick={() => sendSuggestion(s)}>
                    Send
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

{showTransfers && <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.5)",
      zIndex: 1000
    }}>
    <div style={{
        background: "white",
        margin: "5% auto",
        padding: "20px",
        width: "90%",
        maxHeight: "80%",
        overflow: "auto"
      }}>
      <h2>Transfers</h2>

      <button onClick={() => setShowTransfers(false)}>Close</button>

      <table border="1" cellPadding="6" style={{
          width: "100%",
          marginTop: "10px"
        }}>
        <thead>
          <tr>
            <th>Date</th>
            <th>From</th>
            <th>To</th>
            <th>Items</th>
            <th>Picked</th>
            <th>Received</th>
            <th>Completed</th>
            <th>Invoice Sent</th>
            <th>Deleted</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {transferData.map((t, i) => <React.Fragment key={t._id || i}>
            <tr>
              {/* ✅ CREATED DATE + TIME */}
              <td>{formatDateTime(t.createdAt)}</td>

              <td>{t.fromPharmacyName}</td>
              <td>{t.toPharmacyName}</td>

              <td>
                {t.items?.map((it, idx) => <div key={idx}>
                    {it.drug} ({it.qty})
                  </div>)}
              </td>

              {/* ✅ PICKED */}
              <td>
                <span style={{
                    cursor: "pointer"
                  }} onClick={() => setExpandedPicked(prev => ({
                    ...prev,
                    [t._id]: !prev[t._id]
                  }))}>
                  📦 {t.picked?.length || 0}
                </span>

                {expandedPicked[t._id] && <div style={{
                    marginTop: "5px"
                  }}>
                    {t.picked?.map((p, idx) => <div key={idx}>
                        {p.drug} ({p.qty}){" "}
                        {p.timestamp && <span style={{
                        color: "#666"
                      }}>
                            - {formatDateTime(p.timestamp)}
                          </span>}
                      </div>)}
                  </div>}
              </td>

              {/* ✅ RECEIVED */}
              <td>
                <span style={{
                    cursor: "pointer"
                  }} onClick={() => setExpandedReceived(prev => ({
                    ...prev,
                    [t._id]: !prev[t._id]
                  }))}>
                  📥 {t.received?.length || 0}
                </span>

                {(t.received || []).some(r => r.nilDelivery) && <div style={{
                    color: "#CF1322",
                    fontWeight: 700,
                    fontSize: "0.9em"
                  }}>
                    nothing arrived
                  </div>}
                {!(t.received || []).some(r => r.nilDelivery) && (t.received || []).reduce((a, r) => a + (Number(r.variance) || 0), 0) !== 0 && <div style={{
                    color: "#a05a00",
                    fontWeight: 700,
                    fontSize: "0.9em"
                  }}>
                    {(() => {
                      const v = (t.received || []).reduce((a, r) => a + (Number(r.variance) || 0), 0);
                      return `${v > 0 ? "+" : ""}${v} vs sent`;
                    })()}
                  </div>}
                {expandedReceived[t._id] && <div style={{
                    marginTop: "5px"
                  }}>
                    {t.received?.map((r, idx) => <div key={idx} style={{
                      marginBottom: 3
                    }}>
                        {r.drug} ({r.received ?? r.qty ?? 0} of {r.sent ?? "?"}){" "}
                        {r.at && <span style={{
                        color: "#666"
                      }}>
                            - {formatDateTime(r.at)}
                          </span>}
                        {r.note && <div style={{
                        color: "#a05a00"
                      }}>&ldquo;{r.note}&rdquo;</div>}
                      </div>)}
                  </div>}
              </td>

              {/* ✅ COMPLETED WITH TIME */}
              <td>
                {t.status === "completed" ? `✅ ${formatDateTime(t.completedAt)}` : "❌"}
              </td>

              {/* ✅ INVOICE SENT WITH TIME */}
              <td>
                {t.invoiceSent ? `✅ ${formatDateTime(t.invoiceSentAt)}` : "❌"}
              </td>

              {/* ✅ DELETED WITH TIME */}
              <td>
                {t.deleted ? `✅ ${formatDateTime(t.deletedAt)}` : "❌"}
              </td>

              <td>
                <button onClick={() => completeTransfer(t)} disabled={t.deleted || t.status === "completed"}>Complete</button>
                <button onClick={() => deleteTransfer(t)} disabled={t.deleted}>Delete</button>
                <button onClick={() => sendInvoice(t)}>Send Invoice</button>
                {/* The record's own history - raised, who was emailed, what was
                    picked, what the receiving branch said. It has always been
                    written and never shown, which is why a transfer that
                    misbehaved could only be investigated in the database. */}
                <button onClick={() => setExpandedLog(prev => ({
                    ...prev,
                    [t._id]: !prev[t._id]
                  }))} style={{
                    fontWeight: expandedLog[t._id] ? 700 : 400
                  }} title="Show everything that has happened to this transfer">
                  Log{(t.log || []).length ? ` (${t.log.length})` : ""}
                </button>
              </td>
            </tr>

            {expandedLog[t._id] && <tr>
                <td colSpan={10} style={{
                  background: "#FAFAFA"
                }}>
                  <div style={{
                    fontWeight: 700,
                    marginBottom: 6
                  }}>
                    History{t.reference ? ` - ${t.reference}` : ""}
                  </div>
                  {(t.log || []).length === 0 ? <div style={{
                    color: "#5D5D5D"
                  }}>
                      Nothing recorded. Transfers raised before the log was kept have no history.
                    </div> : <ol style={{
                    margin: 0,
                    paddingLeft: 18,
                    lineHeight: 1.6
                  }}>
                      {t.log.map((e, n) => <li key={n} style={{
                      color: /NOT sent|shortfall|SHORTFALL|NOTHING received/i.test(e.action || "") ? "#CF1322" : "#434343"
                    }}>
                          <span style={{
                        color: "#8C8C8C",
                        marginRight: 8
                      }}>
                            {formatDateTime(e.createdAt)}
                          </span>
                          {e.action}
                          {/* Only where the row merges more than one request -
                              otherwise it repeats the heading on every line. */}
                          {e.reference && (t.sourceIds || []).length > 1 && <span style={{
                        color: "#8C8C8C"
                      }}> {"\u00b7"} {e.reference}</span>}
                        </li>)}
                    </ol>}
                </td>
              </tr>}
            </React.Fragment>)}
        </tbody>
      </table>
    </div>
  </div>}


{showUnmatched && <PmrUnmatched groupId={groupId} members={members} onClose={() => setShowUnmatched(false)} />}

{showSearchDialog && <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.5)",
      zIndex: 3000
    }}>
    <div style={{
        background: "white",
        margin: "5% auto",
        padding: "20px",
        width: "700px",
        maxHeight: "80%",
        overflow: "auto"
      }}>
      <div style={{
          position: "sticky",
          top: 0,
          background: "white",
          paddingBottom: "10px",
          marginBottom: "4px",
          borderBottom: "1px solid #eee",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 1
        }}>
        <h2 style={{
            margin: 0
          }}>Search Usage</h2>
        <button onClick={() => setShowSearchDialog(false)}>Close</button>
      </div>

  <div style={{
          marginBottom: "10px"
        }}>
  <input placeholder="Enter drug name to search for" value={searchDrug} onChange={e => setSearchDrug(e.target.value)} style={{
            marginRight: "10px"
          }} />

  <input placeholder="Last 7 digits NHS number" value={searchNhs} onChange={e => {
            const digitsOnly = e.target.value.replace(/\D/g, "");
            setSearchNhs(digitsOnly);
          }} maxLength={7} />

  <button onClick={handleSearchUsage} disabled={searchLoading} style={{
            marginLeft: "10px"
          }}>
    {searchLoading ? "Searching..." : "Search"}
  </button>
</div>

  <div style={{
          background: "#f6f8fa",
          border: "1px solid #dfe3e8",
          padding: "8px 10px",
          marginBottom: "10px",
          fontSize: "0.9em",
          lineHeight: 1.45
        }}>
    <strong>Qty is the amount for that month alone</strong> &mdash; not a running
    total, and not the total for the whole period. Add the months together for that.
    <br />
    Rows with no NHS Fragment come from a PMR usage report. That report covers a date
    range rather than a month, so its total is shared across the months it spans in
    proportion to the days in each. That is why those figures can be fractional, and
    why a 31&#8209;day month reads slightly higher than a 30&#8209;day one. Rows with
    an NHS Fragment come from dispensing data and show the quantity on one
    prescription.
  </div>

      <table border="1" cellPadding="6" style={{
          width: "100%"
        }}>
        <thead>
          <tr>
            <th>Pharmacy</th>
            <th>Month</th>
            <th>Drug</th>
            <th>Qty</th>
            <th>NHS Fragment</th>
          </tr>
        </thead>
        <tbody>
          {searchResults.map((r, i) => <React.Fragment key={i}>
            <tr>
              {/* the row identifies its site by pharmacyId; the name is
                  looked up for display only */}
              <td>{displayName(members.find(m => m.pharmacyId === r.pharmacyId)?.pharmacyName || r.pharmacyId, masked)}</td>
 <td>{formatDispensingMonth(r.dispensingmonth)}</td>
              <td style={{
                  cursor: r.snomed ? "pointer" : "default"
                }} title={r.snomed ? "Show what is counted under this dm+d line" : ""} onClick={() => r.snomed && setOpenMatches(prev => ({
                  ...prev,
                  [`s${i}`]: !prev[`s${i}`]
                }))}>
                {r.snomed ? openMatches[`s${i}`] ? "\u25be " : "\u25b8 " : ""}
                {r.drugname}
              </td>
              <td>{r.qty_used}</td>
              <td>{r.nhsNumber}</td>
            </tr>

            {openMatches[`s${i}`] && <tr>
                <td colSpan={5} style={{
                  padding: 0
                }}>
                  <PmrMatchLines groupId={groupId} snomed={r.snomed} pharmacyId={r.pharmacyId} dispensingmonth={r.dispensingmonth} onChanged={handleSearchUsage} />
                </td>
              </tr>}
            </React.Fragment>)}
        </tbody>
      </table>
    </div>
  </div>}


{neededDialog && <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.5)",
      zIndex: 2000
    }}>
    <div style={{
        background: "white",
        margin: "5% auto",
        padding: "20px",
        width: "500px",
        maxHeight: "70%",
        overflow: "auto"
      }}>
      <h3>Needed By</h3>

      <p><b>{neededDialog.drugName}</b></p>

      {neededDialog.neededByList && neededDialog.neededByList.length > 0 ? neededDialog.neededByList.map((r, i) => <div key={i} style={{
          marginBottom: "6px"
        }}>
            {displayName(r.pharmacyName, masked)} — needs {fmtQty(Math.abs(r.excess), r.qtval)}
            {r.viaGeneric && <span style={{
            color: "#5D5D5D"
          }}> (prescribed generically there)</span>}
          </div>) : <div>No shortages found</div>}

      <button onClick={() => setNeededDialog(null)} style={{
          marginTop: "15px"
        }}>
        Close
      </button>
    </div>
  </div>}





    </div>;
};
export default StockExcess;
